import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, MapPin, X, CalendarDays, Compass, Sparkles, Sun } from 'lucide-react';

const COUNTRY_VALUES = ['Paraguay', 'Argentina', 'Brasil'];
const CITY_VALUES = ['Encarnacion', 'Asuncion', 'Ciudad del Este', 'Posadas', 'Buenos Aires', 'Foz do Iguacu'];
const TYPE_VALUES = ['Hotel', 'Apartment', 'House', 'Cabin'];
const BEDROOM_OPTIONS = [{ value: '1', label: '1+' }, { value: '2', label: '2+' }, { value: '3', label: '3+' }, { value: '4', label: '4+' }];
const GUEST_OPTIONS = [{ value: '1', label: '1+' }, { value: '2', label: '2+' }, { value: '4', label: '4+' }, { value: '6', label: '6+' }];
const SORT_OPTIONS_KEYS = ['recent', 'price_asc', 'price_desc', 'guests_desc'];

const DESTINATION_VALUES = ['Playa', 'Montaña', 'Ciudad', 'Campo', 'Lago', 'Selva'];
const EXPERIENCE_VALUES = ['Familiar', 'Romantica', 'Aventura', 'Relax', 'Negocios', 'Grupo de amigos'];
const SEASON_VALUES = ['Verano', 'Invierno', 'Primavera', 'Otoño', 'Todo el año'];

export function PropertyFilters({ filters, setFilters, setPage, onSearch, onClearFilters, onNavigateMap, events, t, currencySymbol, currency }) {
  const hasFilters = filters.search || filters.city || filters.property_type || filters.min_guests || filters.min_price || filters.max_price || filters.bedrooms || filters.event || filters.destination || filters.experience || filters.season;

  const allOpt = { value: 'all', label: t('properties.filter.all') };
  const countryOpts = [allOpt, ...COUNTRY_VALUES.map(c => ({ value: c, label: c }))];
  const cityOpts = [{ value: 'all', label: t('properties.filter.allCities') }, ...CITY_VALUES.map(c => ({ value: c, label: c }))];
  const typeOpts = [allOpt, ...TYPE_VALUES.map(v => ({ value: v, label: t(`type.${v}`) }))];
  const bedroomOpts = [allOpt, ...BEDROOM_OPTIONS];
  const guestOpts = [allOpt, ...GUEST_OPTIONS];
  const destinationOpts = [allOpt, ...DESTINATION_VALUES.map(d => ({ value: d, label: d }))];
  const experienceOpts = [allOpt, ...EXPERIENCE_VALUES.map(e => ({ value: e, label: e }))];
  const seasonOpts = [allOpt, ...SEASON_VALUES.map(s => ({ value: s, label: s }))];
  const sortOpts = SORT_OPTIONS_KEYS.map(k => ({
    value: k, label: t(`properties.filter.${k === 'recent' ? 'mostRecent' : k === 'price_asc' ? 'priceAsc' : k === 'price_desc' ? 'priceDesc' : 'moreGuests'}`)
  }));

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm mb-8">
      <form onSubmit={onSearch} className="space-y-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
            <Input type="text" placeholder={t('properties.search.placeholder')} className="pl-10 border-[#E7E5E4]"
              value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="search-input" />
          </div>
          <Button type="submit" className="bg-[#264639] hover:bg-[#1C352B]" data-testid="apply-filters-btn">
            <Search className="w-4 h-4 mr-2" /> {t('properties.search.button')}
          </Button>
          <Button type="button" variant="outline" onClick={onNavigateMap} className="border-[#264639] text-[#264639] hover:bg-[#DCE9E2]" data-testid="view-map-btn">
            <MapPin className="w-4 h-4 mr-2" /> {t('properties.viewMap') || 'Ver Mapa'}
          </Button>
          {hasFilters && (
            <Button type="button" variant="outline" onClick={onClearFilters} className="border-[#E7E5E4]" data-testid="clear-filters-btn">
              <X className="w-4 h-4 mr-1" /> {t('properties.filter.clear')}
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <FilterSelect label={t('properties.filter.country')} value={filters.country} testId="country-filter"
            onChange={(v) => { setFilters({ ...filters, country: v }); setPage(0); }}
            options={countryOpts}
            placeholder={t('properties.filter.all')} />

          <FilterSelect label={t('properties.filter.city')} value={filters.city} testId="city-filter"
            onChange={(v) => { setFilters({ ...filters, city: v }); setPage(0); }}
            options={cityOpts}
            placeholder={t('properties.filter.allCities')} />

          <FilterSelect label={t('properties.filter.type')} value={filters.property_type} testId="type-filter"
            onChange={(v) => { setFilters({ ...filters, property_type: v }); setPage(0); }}
            options={typeOpts}
            placeholder={t('properties.filter.all')} />

          <div className="w-32">
            <label className="text-xs text-[#78716C] mb-1 block">{t('properties.filter.priceMin')} ({currencySymbol()})</label>
            <Input type="number" placeholder="0" className="border-[#E7E5E4]" value={filters.min_price}
              onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} data-testid="min-price-filter" />
          </div>

          <div className="w-32">
            <label className="text-xs text-[#78716C] mb-1 block">{t('properties.filter.priceMax')} ({currencySymbol()})</label>
            <Input type="number" placeholder="999" className="border-[#E7E5E4]" value={filters.max_price}
              onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} data-testid="max-price-filter" />
          </div>

          <FilterSelect label={t('properties.filter.bedrooms')} value={filters.bedrooms} testId="bedrooms-filter" width="w-32"
            onChange={(v) => { setFilters({ ...filters, bedrooms: v }); setPage(0); }}
            options={bedroomOpts}
            placeholder={t('properties.filter.all')} />

          <FilterSelect label={t('properties.filter.guests')} value={filters.min_guests} testId="guests-filter" width="w-32"
            onChange={(v) => { setFilters({ ...filters, min_guests: v }); setPage(0); }}
            options={guestOpts}
            placeholder={t('properties.filter.all')} />

          <FilterSelect label={t('properties.filter.sortBy')} value={filters.sort_by} testId="sort-filter" width="w-40"
            onChange={(v) => { setFilters({ ...filters, sort_by: v }); setPage(0); }}
            options={sortOpts}
            placeholder={t('properties.filter.mostRecent')} />

          {events.length > 0 && (
            <div className="w-44">
              <label className="text-xs text-[#78716C] mb-1 block flex items-center gap-1">
                <CalendarDays className="w-3 h-3" /> Evento
              </label>
              <Select value={filters.event} onValueChange={(v) => { setFilters({ ...filters, event: v === 'all' ? '' : v }); setPage(0); }}>
                <SelectTrigger className="border-[#C36F4D] text-[#C36F4D]" data-testid="event-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {events.map(ev => <SelectItem key={ev.event_id} value={ev.event_id}>{ev.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <FilterSelect label={<span className="flex items-center gap-1"><Compass className="w-3 h-3" /> Destino</span>}
            value={filters.destination} testId="destination-filter" width="w-36"
            onChange={(v) => { setFilters({ ...filters, destination: v === 'all' ? '' : v }); setPage(0); }}
            options={destinationOpts} placeholder="Todos" />

          <FilterSelect label={<span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Experiencia</span>}
            value={filters.experience} testId="experience-filter" width="w-44"
            onChange={(v) => { setFilters({ ...filters, experience: v === 'all' ? '' : v }); setPage(0); }}
            options={experienceOpts} placeholder="Todas" />

          <FilterSelect label={<span className="flex items-center gap-1"><Sun className="w-3 h-3" /> Temporada</span>}
            value={filters.season} testId="season-filter" width="w-36"
            onChange={(v) => { setFilters({ ...filters, season: v === 'all' ? '' : v }); setPage(0); }}
            options={seasonOpts} placeholder="Todas" />
        </div>
      </form>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, placeholder, testId, width = 'w-36' }) {
  return (
    <div className={width}>
      <label className="text-xs text-[#78716C] mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="border-[#E7E5E4]" data-testid={testId}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
